import { useToast } from "@/hooks/use-toast";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

interface AppelOffre {
  id: string;
  titre: string;
  reference: string;
  description?: string;
  date_limite: string;
  date_publication?: string;
  date_debut_redaction_ao?: string;
  date_qr_soumissionnaires?: string;
  date_reponses_qr?: string;
  date_analyse_offres?: string;
  date_debut_soutenance?: string;
  date_fin_soutenance?: string;
  date_adjudication?: string;
  date_contractualisation?: string;
  interlocuteur_da?: string;
  ao_manager?: string;
  lien_ao?: string;
  statut?: string;
  competences_html?: string | null;
  certifications_html?: string | null;
  poids_competences_pct?: number;
  poids_certifications_pct?: number;
  seuil_shortlist?: number;
  seuil_consider?: number;
}

interface Evaluation {
  id: string;
  nom: string;
  description?: string;
  ponderation_globale_pct: number;
}

interface CritereEvaluation {
  id: string;
  nom: string;
  description?: string;
  ponderation_pct: number;
  contenu_html?: string;
}

interface Stats {
  totalCV: number;
  totalAnalyses: number;
  shortlist: number;
  consider: number;
  reject: number;
}

interface Service {
  id: string;
  nom: string;
}

interface SocieteNote {
  societe_nom: string;
  note_qualite_offre: number | null;
  note_prix: number | null;
  note_durabilite: number | null;
  note_references: number | null;
  poids_qualite_offre: number | null;
  poids_prix: number | null;
  poids_durabilite: number | null;
  poids_references: number | null;
  score_pondere: number | null;
  commentaire: string | null;
}

export const useAOPdfExport = () => {
  const { toast } = useToast();

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return "Non défini";
    try {
      return format(new Date(dateString), "dd MMMM yyyy", { locale: fr });
    } catch {
      return dateString;
    }
  };

  const stripHtml = (html: string | null | undefined): string => {
    if (!html) return "";
    return html.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
  };

  const getStatusLabel = (statut?: string) => {
    switch (statut) {
      case "en cours": return "En cours";
      case "publié": return "Publié";
      case "terminé": return "Terminé";
      default: return "Inconnu";
    }
  };

  const exportAOToPdf = async (
    appelOffre: AppelOffre,
    evaluations: Evaluation[],
    criteresMap: Record<string, CritereEvaluation[]>,
    stats: Stats,
    services: Service[],
    societeNotes?: SocieteNote[]
  ) => {
    try {
      const doc = new jsPDF();
      let yPosition = 20;

      // Colors
      const primaryColor: [number, number, number] = [59, 130, 246];
      const headerBg: [number, number, number] = [241, 245, 249];
      const textColor: [number, number, number] = [30, 41, 59];

      // Title
      doc.setFontSize(22);
      doc.setTextColor(...primaryColor);
      doc.text("Fiche Appel d'Offres", 105, yPosition, { align: "center" });
      yPosition += 15;

      // Reference and Status
      doc.setFontSize(14);
      doc.setTextColor(...textColor);
      doc.text(appelOffre.titre, 105, yPosition, { align: "center" });
      yPosition += 8;
      
      doc.setFontSize(11);
      doc.setTextColor(100, 100, 100);
      doc.text(`Référence: ${appelOffre.reference} | Statut: ${getStatusLabel(appelOffre.statut)}`, 105, yPosition, { align: "center" });
      yPosition += 15;

      // Horizontal line
      doc.setDrawColor(...primaryColor);
      doc.setLineWidth(0.5);
      doc.line(20, yPosition, 190, yPosition);
      yPosition += 10;

      // Section: Informations générales
      doc.setFontSize(14);
      doc.setTextColor(...primaryColor);
      doc.text("1. Informations Générales", 20, yPosition);
      yPosition += 8;

      const generalInfoData = [
        ["Titre", appelOffre.titre],
        ["Référence", appelOffre.reference],
        ["Description", appelOffre.description || "Non renseigné"],
        ["Interlocuteur DA", appelOffre.interlocuteur_da || "Non renseigné"],
        ["Responsable AO", appelOffre.ao_manager || "Non renseigné"],
        ["Lien AO", appelOffre.lien_ao || "Non renseigné"],
      ];

      autoTable(doc, {
        startY: yPosition,
        head: [],
        body: generalInfoData,
        theme: "plain",
        styles: { fontSize: 10, cellPadding: 3 },
        columnStyles: {
          0: { fontStyle: "bold", cellWidth: 50 },
          1: { cellWidth: 120 }
        },
        margin: { left: 20, right: 20 }
      });

      yPosition = (doc as any).lastAutoTable.finalY + 15;

      // Section: Planning
      doc.setFontSize(14);
      doc.setTextColor(...primaryColor);
      doc.text("2. Planning du Projet", 20, yPosition);
      yPosition += 8;

      const planningData = [
        ["Début rédaction", formatDate(appelOffre.date_debut_redaction_ao)],
        ["Publication", formatDate(appelOffre.date_publication)],
        ["Date limite", formatDate(appelOffre.date_limite)],
        ["Q/R Soumissionnaires", formatDate(appelOffre.date_qr_soumissionnaires)],
        ["Réponses Q/R", formatDate(appelOffre.date_reponses_qr)],
        ["Analyse des offres", formatDate(appelOffre.date_analyse_offres)],
        ["Début soutenance", formatDate(appelOffre.date_debut_soutenance)],
        ["Fin soutenance", formatDate(appelOffre.date_fin_soutenance)],
        ["Adjudication", formatDate(appelOffre.date_adjudication)],
        ["Contractualisation", formatDate(appelOffre.date_contractualisation)],
      ];

      autoTable(doc, {
        startY: yPosition,
        head: [["Phase", "Date"]],
        body: planningData,
        theme: "striped",
        headStyles: { 
          fillColor: primaryColor, 
          textColor: [255, 255, 255],
          fontStyle: "bold"
        },
        styles: { fontSize: 10, cellPadding: 4 },
        columnStyles: {
          0: { cellWidth: 70 },
          1: { cellWidth: 70 }
        },
        margin: { left: 20, right: 20 }
      });

      yPosition = (doc as any).lastAutoTable.finalY + 15;

      // Check if we need a new page
      if (yPosition > 240) {
        doc.addPage();
        yPosition = 20;
      }

      // Section: Statistiques
      doc.setFontSize(14);
      doc.setTextColor(...primaryColor);
      doc.text("3. Statistiques", 20, yPosition);
      yPosition += 8;

      const statsData = [
        ["Documents importés", stats.totalCV.toString()],
        ["Analyses effectuées", stats.totalAnalyses.toString()],
        ["Shortlist", `${stats.shortlist} (≥${appelOffre.seuil_shortlist || 75} pts)`],
        ["À considérer", `${stats.consider} (${appelOffre.seuil_consider || 60}-${(appelOffre.seuil_shortlist || 75) - 1} pts)`],
        ["Rejetés", stats.reject.toString()],
      ];

      autoTable(doc, {
        startY: yPosition,
        head: [],
        body: statsData,
        theme: "plain",
        styles: { fontSize: 10, cellPadding: 3 },
        columnStyles: {
          0: { fontStyle: "bold", cellWidth: 60 },
          1: { cellWidth: 60 }
        },
        margin: { left: 20, right: 20 }
      });

      yPosition = (doc as any).lastAutoTable.finalY + 15;

      // Check if we need a new page
      if (yPosition > 200) {
        doc.addPage();
        yPosition = 20;
      }

      // Section: Évaluations et critères
      doc.setFontSize(14);
      doc.setTextColor(...primaryColor);
      doc.text("4. Critères d'Évaluation", 20, yPosition);
      yPosition += 8;

      if (evaluations.length > 0) {
        for (const evaluation of evaluations) {
          // Check if we need a new page
          if (yPosition > 240) {
            doc.addPage();
            yPosition = 20;
          }

          doc.setFontSize(12);
          doc.setTextColor(...textColor);
          doc.text(`${evaluation.nom} (${evaluation.ponderation_globale_pct}%)`, 25, yPosition);
          yPosition += 6;

          const criteres = criteresMap[evaluation.id] || [];
          if (criteres.length > 0) {
            const criteresData = criteres.map(c => [
              c.nom,
              `${c.ponderation_pct}%`,
              stripHtml(c.contenu_html)
            ]);

            autoTable(doc, {
              startY: yPosition,
              head: [["Critère", "Poids", "Description"]],
              body: criteresData,
              theme: "striped",
              headStyles: { 
                fillColor: headerBg as [number, number, number], 
                textColor: textColor,
                fontStyle: "bold"
              },
              styles: { fontSize: 9, cellPadding: 3, overflow: 'linebreak' },
              columnStyles: {
                0: { cellWidth: 40 },
                1: { cellWidth: 15 },
                2: { cellWidth: 'auto' }
              },
              margin: { left: 25, right: 20 }
            });

            yPosition = (doc as any).lastAutoTable.finalY + 10;
          } else {
            doc.setFontSize(9);
            doc.setTextColor(100, 100, 100);
            doc.text("Aucun critère défini", 30, yPosition);
            yPosition += 8;
          }
        }
      } else {
        doc.setFontSize(10);
        doc.setTextColor(100, 100, 100);
        doc.text("Aucune évaluation configurée", 25, yPosition);
        yPosition += 10;
      }

      // Section: Notes par société
      if (societeNotes && societeNotes.length > 0) {
        // Check if we need a new page
        if (yPosition > 180) {
          doc.addPage();
          yPosition = 20;
        }

        doc.setFontSize(14);
        doc.setTextColor(...primaryColor);
        doc.text("5. Notes par Société", 20, yPosition);
        yPosition += 8;

        // Get weights from first note (they should all be the same)
        const firstNote = societeNotes[0];
        const weights = {
          qualite: firstNote.poids_qualite_offre ?? 25,
          prix: firstNote.poids_prix ?? 25,
          durabilite: firstNote.poids_durabilite ?? 25,
          references: firstNote.poids_references ?? 25
        };

        // Sort by weighted score descending
        const sortedNotes = [...societeNotes].sort((a, b) => 
          (b.score_pondere ?? 0) - (a.score_pondere ?? 0)
        );

        const notesData = sortedNotes.map((note, index) => [
          (index + 1).toString(),
          note.societe_nom,
          note.note_qualite_offre !== null ? note.note_qualite_offre.toString() : '-',
          note.note_prix !== null ? note.note_prix.toString() : '-',
          note.note_durabilite !== null ? note.note_durabilite.toString() : '-',
          note.note_references !== null ? note.note_references.toString() : '-',
          note.score_pondere !== null ? note.score_pondere.toFixed(2) : '-'
        ]);

        autoTable(doc, {
          startY: yPosition,
          head: [[
            '#',
            'Société',
            `Qualité\n(${weights.qualite}%)`,
            `Prix\n(${weights.prix}%)`,
            `Durabilité\n(${weights.durabilite}%)`,
            `Références\n(${weights.references}%)`,
            'Score\nPondéré'
          ]],
          body: notesData,
          theme: "striped",
          headStyles: { 
            fillColor: primaryColor, 
            textColor: [255, 255, 255],
            fontStyle: "bold",
            halign: "center",
            fontSize: 8
          },
          styles: { fontSize: 9, cellPadding: 3, halign: "center" },
          columnStyles: {
            0: { cellWidth: 10 },
            1: { cellWidth: 50, halign: "left" },
            2: { cellWidth: 25 },
            3: { cellWidth: 20 },
            4: { cellWidth: 25 },
            5: { cellWidth: 25 },
            6: { cellWidth: 25, fontStyle: "bold" }
          },
          margin: { left: 20, right: 20 },
          didDrawCell: (data: any) => {
            // Highlight top 3 scores
            if (data.section === 'body' && data.column.index === 6 && data.row.index < 3) {
              const colors: [number, number, number][] = [
                [255, 215, 0],   // Gold
                [192, 192, 192], // Silver
                [205, 127, 50]   // Bronze
              ];
              doc.setFillColor(...colors[data.row.index]);
              doc.roundedRect(data.cell.x + 1, data.cell.y + 1, data.cell.width - 2, data.cell.height - 2, 1, 1, 'F');
              doc.setTextColor(0, 0, 0);
              doc.text(data.cell.text[0], data.cell.x + data.cell.width / 2, data.cell.y + data.cell.height / 2 + 3, { align: 'center' });
            }
          }
        });

        yPosition = (doc as any).lastAutoTable.finalY + 10;

        // Add comments if any
        const notesWithComments = sortedNotes.filter(n => n.commentaire && n.commentaire.trim() !== '');
        if (notesWithComments.length > 0) {
          if (yPosition > 240) {
            doc.addPage();
            yPosition = 20;
          }
          
          doc.setFontSize(11);
          doc.setTextColor(...textColor);
          doc.text("Commentaires:", 20, yPosition);
          yPosition += 6;

          notesWithComments.forEach(note => {
            if (yPosition > 270) {
              doc.addPage();
              yPosition = 20;
            }
            doc.setFontSize(9);
            doc.setTextColor(...primaryColor);
            doc.text(`• ${note.societe_nom}:`, 25, yPosition);
            doc.setTextColor(...textColor);
            const commentLines = doc.splitTextToSize(note.commentaire || '', 150);
            yPosition += 4;
            commentLines.forEach((line: string) => {
              doc.text(line, 30, yPosition);
              yPosition += 4;
            });
            yPosition += 2;
          });
        }
      }

      // New page for Gantt (landscape)
      doc.addPage("landscape");
      yPosition = 20;

      // Section: Gantt Timeline
      doc.setFontSize(14);
      doc.setTextColor(...primaryColor);
      doc.text(societeNotes && societeNotes.length > 0 ? "6. Planning Gantt du Projet" : "5. Planning Gantt du Projet", 20, yPosition);
      yPosition += 5;
      
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.text("Vue chronologique avec granularité mensuelle", 20, yPosition);
      yPosition += 12;

      // Define Gantt phases with colors
      const ganttPhases = [
        { phase: "Réd.", fullName: "Rédaction AO", start: appelOffre.date_debut_redaction_ao, end: appelOffre.date_publication, color: [249, 115, 22] as [number, number, number] },
        { phase: "Q&R", fullName: "Q/R Soumissionnaires", start: appelOffre.date_qr_soumissionnaires, end: appelOffre.date_reponses_qr, color: [168, 85, 247] as [number, number, number] },
        { phase: "Analyse", fullName: "Analyse des offres", start: appelOffre.date_analyse_offres, end: appelOffre.date_debut_soutenance, color: [6, 182, 212] as [number, number, number] },
        { phase: "Soutenance", fullName: "Soutenance", start: appelOffre.date_debut_soutenance, end: appelOffre.date_fin_soutenance, color: [236, 72, 153] as [number, number, number] },
        { phase: "Adjud.", fullName: "Adjudication", start: appelOffre.date_fin_soutenance, end: appelOffre.date_adjudication, color: [16, 185, 129] as [number, number, number] },
        { phase: "Contract.", fullName: "Contractualisation", start: appelOffre.date_adjudication, end: appelOffre.date_contractualisation, color: [217, 119, 6] as [number, number, number] },
      ].filter(p => p.start && p.end);

      // Define milestone dates (single date markers)
      const milestones = [
        { name: "Publication AO", date: appelOffre.date_publication, color: [59, 130, 246] as [number, number, number] },
        { name: "Date limite soumission", date: appelOffre.date_limite, color: [239, 68, 68] as [number, number, number] },
      ].filter(m => m.date);

      if (ganttPhases.length > 0 || milestones.length > 0) {
        // Find date range for all phases and milestones
        const phaseDates = ganttPhases.flatMap(p => [new Date(p.start!), new Date(p.end!)]);
        const milestoneDates = milestones.map(m => new Date(m.date!));
        const allDates = [...phaseDates, ...milestoneDates];
        const minDate = new Date(Math.min(...allDates.map(d => d.getTime())));
        const maxDate = new Date(Math.max(...allDates.map(d => d.getTime())));
        
        // Extend to full months
        minDate.setDate(1);
        maxDate.setMonth(maxDate.getMonth() + 1);
        maxDate.setDate(0);

        // Calculate months array
        const months: { date: Date; label: string }[] = [];
        const current = new Date(minDate);
        while (current <= maxDate) {
          months.push({
            date: new Date(current),
            label: format(current, "MMM", { locale: fr })
          });
          current.setMonth(current.getMonth() + 1);
        }

        // Gantt dimensions - dynamic scaling based on number of months
        const ganttLeft = 55;
        const maxGanttWidth = 220; // Max width available in landscape
        const minMonthWidth = 8; // Minimum width per month for readability
        const idealMonthWidth = 18; // Ideal width per month
        
        // Calculate optimal width
        const ganttWidth = Math.min(maxGanttWidth, Math.max(months.length * minMonthWidth, months.length * idealMonthWidth));
        const monthWidth = ganttWidth / months.length;
        const rowHeight = 20;
        const barHeight = 14;

        // Draw header with months - show year labels for multi-year projects
        doc.setFillColor(241, 245, 249);
        doc.rect(ganttLeft, yPosition, ganttWidth, 12, 'F');
        
        // Determine if we should show year (for projects > 12 months)
        const showYear = months.length > 12;
        
        doc.setFontSize(showYear ? 6 : 8);
        doc.setTextColor(...textColor);
        let lastYear = -1;
        months.forEach((month, index) => {
          const x = ganttLeft + (index * monthWidth) + (monthWidth / 2);
          const currentYear = month.date.getFullYear();
          
          // Show month label, and year if it changes
          let label = month.label;
          if (showYear && currentYear !== lastYear) {
            label = `${month.label} ${currentYear.toString().slice(-2)}`;
            lastYear = currentYear;
          } else if (showYear) {
            label = month.label;
          }
          
          doc.text(label, x, yPosition + 8, { align: "center" });
          // Draw vertical grid line
          doc.setDrawColor(220, 220, 220);
          doc.setLineWidth(0.2);
          doc.line(ganttLeft + (index * monthWidth), yPosition, ganttLeft + (index * monthWidth), yPosition + 12 + rowHeight);
        });
        // Last vertical line
        doc.line(ganttLeft + ganttWidth, yPosition, ganttLeft + ganttWidth, yPosition + 12 + rowHeight);
        
        // Draw header border
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.3);
        doc.rect(ganttLeft, yPosition, ganttWidth, 12);
        
        yPosition += 12;

        // Draw AO row
        const rowY = yPosition;
        
        // AO name on the left
        doc.setFontSize(8);
        doc.setTextColor(59, 130, 246);
        doc.text(appelOffre.titre.substring(0, 20) + (appelOffre.titre.length > 20 ? "..." : ""), 15, rowY + 10);
        doc.setFontSize(6);
        doc.setTextColor(100, 100, 100);
        doc.text(appelOffre.reference.substring(0, 15), 15, rowY + 16);

        // Draw row background
        doc.setFillColor(250, 250, 250);
        doc.rect(ganttLeft, rowY, ganttWidth, rowHeight, 'F');
        doc.setDrawColor(220, 220, 220);
        doc.rect(ganttLeft, rowY, ganttWidth, rowHeight);

        // Calculate time range in milliseconds
        const timeStart = minDate.getTime();
        const timeEnd = maxDate.getTime();
        const timeRange = timeEnd - timeStart;

        // Draw each phase bar
        ganttPhases.forEach((phase) => {
          const phaseStart = new Date(phase.start!).getTime();
          const phaseEnd = new Date(phase.end!).getTime();
          
          const startX = ganttLeft + ((phaseStart - timeStart) / timeRange) * ganttWidth;
          const endX = ganttLeft + ((phaseEnd - timeStart) / timeRange) * ganttWidth;
          const barWidth = Math.max(endX - startX, 8); // Minimum width for visibility
          
          const barY = rowY + (rowHeight - barHeight) / 2;
          
          // Draw bar with rounded corners
          doc.setFillColor(...phase.color);
          doc.roundedRect(startX, barY, barWidth, barHeight, 2, 2, 'F');
          
          // Draw phase label on bar if wide enough
          if (barWidth > 20) {
            doc.setFontSize(6);
            doc.setTextColor(255, 255, 255);
            doc.text(phase.phase, startX + barWidth / 2, barY + barHeight / 2 + 2, { align: "center" });
          }
        });

        // Draw milestone markers (diamond/triangle shapes)
        milestones.forEach((milestone) => {
          const milestoneTime = new Date(milestone.date!).getTime();
          const x = ganttLeft + ((milestoneTime - timeStart) / timeRange) * ganttWidth;
          const centerY = rowY + rowHeight / 2;
          
          // Draw diamond shape
          doc.setFillColor(...milestone.color);
          const size = 4;
          doc.triangle(
            x, centerY - size,     // top
            x + size, centerY,     // right
            x, centerY + size      // bottom (will be completed by next triangle)
          );
          doc.triangle(
            x, centerY - size,     // top
            x - size, centerY,     // left
            x, centerY + size      // bottom
          );
        });

        yPosition += rowHeight + 15;

        // Draw legend for phases
        doc.setFontSize(10);
        doc.setTextColor(...textColor);
        doc.text("Légende:", 20, yPosition);
        yPosition += 8;

        let xPos = 20;
        ganttPhases.forEach((phase, index) => {
          if (index > 0 && index % 4 === 0) {
            xPos = 20;
            yPosition += 10;
          }
          doc.setFillColor(...phase.color);
          doc.roundedRect(xPos, yPosition - 4, 12, 6, 1, 1, 'F');
          doc.setFontSize(8);
          doc.setTextColor(...textColor);
          doc.text(phase.fullName, xPos + 15, yPosition);
          xPos += 65;
        });

        // Add milestones to legend
        if (milestones.length > 0) {
          if (ganttPhases.length % 4 !== 0 || ganttPhases.length === 0) {
            // Continue on same line if space available
          } else {
            xPos = 20;
            yPosition += 10;
          }
          
          milestones.forEach((milestone, index) => {
            if ((ganttPhases.length + index) % 4 === 0 && (ganttPhases.length + index) > 0) {
              xPos = 20;
              yPosition += 10;
            }
            // Draw diamond marker in legend
            doc.setFillColor(...milestone.color);
            const legendX = xPos + 6;
            const legendY = yPosition - 1;
            doc.triangle(legendX, legendY - 3, legendX + 3, legendY, legendX, legendY + 3);
            doc.triangle(legendX, legendY - 3, legendX - 3, legendY, legendX, legendY + 3);
            doc.setFontSize(8);
            doc.setTextColor(...textColor);
            doc.text(milestone.name, xPos + 15, yPosition);
            xPos += 65;
          });
        }

        yPosition += 15;

        // Add dates table below
        doc.setFontSize(10);
        doc.setTextColor(...primaryColor);
        doc.text("Détail des dates:", 20, yPosition);
        yPosition += 5;

        const ganttData = [
          ...ganttPhases.map(p => [
            p.fullName,
            formatDate(p.start),
            formatDate(p.end),
            calculateDuration(p.start!, p.end!)
          ]),
          ...milestones.map(m => [
            m.name,
            formatDate(m.date),
            "-",
            "Jalon"
          ])
        ];

        autoTable(doc, {
          startY: yPosition,
          head: [["Phase", "Date début", "Date fin", "Durée"]],
          body: ganttData,
          theme: "striped",
          headStyles: { 
            fillColor: primaryColor, 
            textColor: [255, 255, 255],
            fontStyle: "bold"
          },
          styles: { fontSize: 9, cellPadding: 4 },
          columnStyles: {
            0: { cellWidth: 55 },
            1: { cellWidth: 45 },
            2: { cellWidth: 45 },
            3: { cellWidth: 25 }
          },
          margin: { left: 20, right: 20 }
        });
      } else {
        doc.setFontSize(10);
        doc.setTextColor(100, 100, 100);
        doc.text("Aucune date de planning configurée", 25, yPosition);
      }

      // Footer on all pages
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(
          `Généré le ${format(new Date(), "dd/MM/yyyy à HH:mm", { locale: fr })} - Page ${i}/${pageCount}`,
          105,
          285,
          { align: "center" }
        );
      }

      // Save PDF
      const fileName = `AO_${appelOffre.reference.replace(/[^a-zA-Z0-9]/g, "_")}_${format(new Date(), "yyyyMMdd")}.pdf`;
      doc.save(fileName);

      toast({
        title: "PDF généré",
        description: `Le fichier ${fileName} a été téléchargé`
      });
    } catch (error) {
      console.error("Erreur lors de la génération du PDF:", error);
      toast({
        title: "Erreur",
        description: "Impossible de générer le PDF",
        variant: "destructive"
      });
    }
  };

  const calculateDuration = (start: string, end: string): string => {
    try {
      const startDate = new Date(start);
      const endDate = new Date(end);
      const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return `${diffDays}j`;
    } catch {
      return "-";
    }
  };

  return { exportAOToPdf };
};
